<template>
  <section class="bg-white py-20 lg:py-28" :id="id">
    <div class="mx-auto max-w-[1200px] px-6 lg:px-10">
      <div class="relative overflow-hidden rounded-[2rem] border border-text-main/10 bg-background-light px-6 py-14 sm:px-8 lg:px-16 lg:py-20">
        <div class="pointer-events-none absolute -right-10 -top-10 h-64 w-64 rounded-full bg-primary/15 blur-3xl" aria-hidden="true"></div>

        <div class="relative">
          <div class="mx-auto max-w-[680px] text-center">
            <h2 class="text-3xl font-black leading-[1.05] tracking-[-0.02em] text-text-main sm:text-4xl">
              {{ t('testimonial.title') }}
            </h2>
          </div>

          <!-- Grid adapts to how many testimonials are filled in: one reads as a
               single wide quote, three as a row of cards. -->
          <div class="mt-12" :class="gridClass">
            <figure
              v-for="item in visibleTestimonials"
              :key="item.id"
              class="flex h-full flex-col rounded-3xl border border-text-main/10 bg-white p-7 lg:p-8"
              :class="{ 'border-dashed border-text-main/25': item.draft }"
            >
              <span class="text-3xl font-black leading-none text-primary" aria-hidden="true">&ldquo;</span>

              <blockquote class="mt-3 flex-1 text-base leading-relaxed text-text-main lg:text-lg">
                {{ content(item).quote }}
              </blockquote>

              <figcaption class="mt-7 flex items-center gap-3 border-t border-text-main/10 pt-6">
                <img
                  v-if="item.image"
                  :src="item.image"
                  :alt="item.name"
                  class="h-11 w-11 shrink-0 rounded-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <span
                  v-else
                  class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-black text-text-main"
                  :class="{ 'border border-dashed border-text-main/25 bg-transparent': item.draft }"
                  aria-hidden="true"
                >{{ initials(item.name) }}</span>

                <span class="min-w-0">
                  <span class="block truncate font-bold text-text-main">{{ item.name }}</span>
                  <span class="block truncate text-sm text-text-muted">
                    {{ content(item).role }}, {{ item.company }}
                  </span>
                </span>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { testimonials, type Testimonial } from '../../data/testimonials';

defineProps<{ id?: string }>();

const { t, locale } = useI18n();

// Unfinished placeholder quotes stay on the dev server only.
const visibleTestimonials = computed(() =>
  testimonials.filter((item) => import.meta.env.DEV || !item.draft)
);

const gridClass = computed(() => {
  const count = visibleTestimonials.value.length;
  if (count <= 1) return 'mx-auto max-w-[760px]';
  if (count === 2) return 'grid gap-6 md:grid-cols-2';
  return 'grid gap-6 md:grid-cols-2 lg:grid-cols-3';
});

const content = (item: Testimonial) => (locale.value === 'nl' ? item.nl : item.en);

// Monogram fallback for clients who have not supplied a portrait.
const initials = (name: string) =>
  name
    .replace(/\[.*?\]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';
</script>
